<template>
  <component
    :is="props.rootTag || 'section'"
    :class="cn('template-max-height relative flex h-screen', props.classes?.root, props.class)"
    :style="{ backgroundColor: props.bgColor || '#E5DDF9' }"
  >
    <div
      :class="
        cn(
          'template-max-width absolute start-1/2 z-0 h-full w-full translate-x-1/2 object-cover ltr:-translate-x-1/2',
          props.classes?.imageWrapper
        )
      "
    >
      <UiResponsiveImage
        :class="cn('h-full', props.classes?.image)"
        :preload="props.preloadImage"
        :src="`/homepage/redesign/${locale}/${props.imagePath}-desktop.avif`"
        :mobile-src="`/homepage/redesign/${locale}/${props.imagePath}-mobile.avif`"
        :alt="props.imageAlt || props.title || 'Background image'"
      />
    </div>

    <div
      :class="
        cn(
          'z-1 md:max-w-large container grid grid-cols-2 items-center gap-24 md:flex md:flex-col',
          props.classes?.textWrapper
        )
      "
    >
      <div :class="cn('flex flex-col md:pt-32', props.classes?.textInnerWrapper)">
        <component
          v-html-format="props.title"
          :class="cn('text-3xl font-semibold', props.classes?.title)"
          :is="titleTag ?? 'span'"
        />

        <p v-html-format="props.description" :class="cn('mt-2 font-medium', props.classes?.description)" />

        <div :class="cn('mt-8 flex gap-6', props.classes?.ctaWrapper)">
          <UiButton v-if="props.primaryCta?.hidden !== true" v-bind="primaryCtaProps">
            {{ props.primaryCta?.text || t("defaultPrimaryCtaText") }}
          </UiButton>

          <UiButton
            v-if="props.secondaryCta?.hidden !== true"
            v-bind="secondaryCtaProps"
            :variant="props.secondaryCta?.variant || 'link'"
            :class="cn('p-0 text-base font-medium', props.secondaryCta?.class)"
          >
            {{ props.secondaryCta?.text || t("defaultSecondaryCtaText") }}
          </UiButton>
        </div>
      </div>
    </div>
  </component>
</template>

<script setup lang="ts">
import { type UiButtonProps } from "@finq/ui/components/ui/Button.vue"

interface CtaButton extends UiButtonProps {
  text?: string
  hidden?: boolean
}

export interface HomepageSectionTemplateClasses {
  root?: ClassValue
  imageWrapper?: ClassValue
  image?: ClassValue
  textWrapper?: ClassValue
  textInnerWrapper?: ClassValue
  title?: ClassValue
  description?: ClassValue
  ctaWrapper?: ClassValue
}

export interface HomepageSectionTemplateProps {
  rootTag?: string
  class?: ClassValue
  bgColor?: string
  imagePath: string
  imageAlt?: string
  title: string
  description: string
  primaryCta?: CtaButton
  secondaryCta?: CtaButton
  titleTag?: string
  preloadImage?: boolean
  classes?: Partial<HomepageSectionTemplateClasses>
}

const props = defineProps<Partial<HomepageSectionTemplateProps>>()

const { t, locale } = useI18n({ useScope: "local" })

const primaryCtaProps = computed(() => {
  const { text, ...rest } = props.primaryCta || {}
  return rest
})

const secondaryCtaProps = computed(() => {
  const { text, ...rest } = props.secondaryCta || {}
  return rest
})
</script>

<style lang="scss" scoped>
.template-max-height {
  max-height: 1440px;
}
.template-max-width {
  max-width: 2560px;
}
</style>

<i18n lang="json">
{
  "he": {
    "defaultPrimaryCtaText": "אני רוצה להתחיל",
    "defaultSecondaryCtaText": "עוד פרטים"
  },
  "en": {
    "defaultPrimaryCtaText": "Get started",
    "defaultSecondaryCtaText": "More details"
  }
}
</i18n>
